/**
 * DiscoverySequenceConfigV2 - Material-UI integrated version of DiscoverySequenceSettings
 * Provides comprehensive discovery sequence management within the settings modal
 */

import React, { useState } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { grey } from '@mui/material/colors';
import DiscoverySequenceSettingsStyled from './DiscoverySequenceSettingsStyled';

const DiscoverySequenceConfigV2 = ({ onSave, onClose, userId: propUserId }) => {
  const [showFullSettings, setShowFullSettings] = useState(false);
  const [userId, setUserId] = useState(propUserId || 'default_user');
  const [error, setError] = useState(null);

  // Use the passed userId prop, or fall back to detection methods
  React.useEffect(() => {
    if (propUserId) {
      setUserId(propUserId);
      return;
    }

    const getUserId = () => {
      // Try to get from localStorage
      const storedUserId = localStorage.getItem('userId') || localStorage.getItem('user_id');
      if (storedUserId) {
        setUserId(storedUserId);
        return;
      }

      // Try to get from sessionStorage
      const sessionUserId = sessionStorage.getItem('userId') || sessionStorage.getItem('user_id');
      if (sessionUserId) {
        setUserId(sessionUserId);
        return;
      }

      // Try to get from URL params
      const urlParams = new URLSearchParams(window.location.search);
      const urlUserId = urlParams.get('userId') || urlParams.get('user_id');
      if (urlUserId) {
        setUserId(urlUserId);
        return;
      }

      console.warn('Could not determine user ID, using default_user');
    };

    getUserId();
  }, [propUserId]);

  const handleOpenFullSettings = () => {
    setShowFullSettings(true);
  };

  const handleCloseFullSettings = () => {
    setShowFullSettings(false);
    setError(null);
  };

  if (showFullSettings) {
    return (
      <DiscoverySequenceSettingsStyled 
        userId={userId}
        onClose={handleCloseFullSettings}
      />
    );
  }

  return (
    <Box sx={{ p: 3, color: grey[300] }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold" }}>
        Discovery Sequence Management
      </Typography>
      
      <Typography variant="body2" sx={{ mb: 3, color: grey[400] }}>
        Configure custom discovery sequences for different role types. Create personalized flows,
        reorder topics with drag-and-drop, and manage sequence templates.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3, backgroundColor: '#3d1e1e', color: grey[300] }}>
          {error}
        </Alert>
      )}

      <Box sx={{ 
        border: `1px solid ${grey[700]}`, 
        borderRadius: 2, 
        p: 3, 
        mb: 3,
        backgroundColor: '#2d3748'
      }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
          Current Features:
        </Typography>
        
        <Box component="ul" sx={{ 
          m: 0, 
          pl: 2, 
          color: grey[400],
          '& li': { mb: 1 }
        }}>
          <li>Create and manage custom discovery sequences</li>
          <li>Drag-and-drop topic reordering</li>
          <li>Enable/disable topics and individual components</li>
          <li>Clone and template sequences across role types</li>
          <li>Set default sequences for each role type</li>
          <li>Real-time preview and validation</li>
        </Box>
      </Box>

      <Box sx={{ 
        border: `1px solid ${grey[700]}`, 
        borderRadius: 2, 
        p: 3, 
        mb: 3,
        backgroundColor: '#1f2937'
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: grey[300] }}>
          User ID: {userId}
        </Typography>
        <Typography variant="body2" sx={{ color: grey[500], fontSize: '0.875rem' }}>
          Sequences will be managed for this user account
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Button
          variant="outlined"
          onClick={() => setError('Legacy discovery sequence management is deprecated. Please use the new interface.')}
          disabled
          sx={{ 
            color: grey[500], 
            borderColor: grey[700],
            '&:disabled': {
              color: grey[600],
              borderColor: grey[800]
            }
          }}
        >
          Legacy Configuration
        </Button>
        
        <Button
          variant="contained"
          onClick={handleOpenFullSettings}
          sx={{ 
            backgroundColor: '#3182ce',
            '&:hover': {
              backgroundColor: '#2c5282'
            }
          }}
        >
          Open Sequence Manager
        </Button>
      </Box>

      <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${grey[800]}` }}>
        <Typography variant="body2" sx={{ color: grey[500], fontSize: '0.75rem' }}>
          💡 Tip: The new sequence manager provides a comprehensive interface for creating 
          and managing discovery flows with support for all role types and advanced customization options.
        </Typography>
      </Box>
    </Box>
  );
};

export default DiscoverySequenceConfigV2;